/**
 * Run statuses after which nothing moves on its own (delivery may still settle). RESET is NOT
 * here: the backend keeps it in-flight (a reset run is being re-driven), so polling continues.
 */
const TERMINAL_RUN_STATUSES = new Set(['COMPLETED', 'FAILED', 'TERMINATED', 'SKIPPED']);

/** True while a run can still change without a user action — drives progress polling. */
export function isRunLive(runStatus: string | null | undefined): boolean {
  return !!runStatus && !TERMINAL_RUN_STATUSES.has(runStatus);
}

/** Statuses a batch/experiment run can only reach by stopping — the ONE failure status section
 *  (header) shows for these, and only these (never for a clean COMPLETED or an open gate).
 *  REJECTED is a run rejected at a review gate — stopped the same way FAILED/TERMINATED are. */
const STOPPED_RUN_STATUSES = new Set(['FAILED', 'TERMINATED', 'REJECTED']);

/** True once the run has stopped without finishing — drives the header's one failure section. */
export function isRunStopped(runStatus: string | null | undefined): boolean {
  return !!runStatus && STOPPED_RUN_STATUSES.has(runStatus);
}

/** Tabs that split a plain `RunStatus` into a more specific list-only state. */
type SplitTab = 'DELIVERING' | 'REJECTED';
const SPLIT_TABS: ReadonlySet<string> = new Set<SplitTab>(['DELIVERING', 'REJECTED']);

/**
 * Finding #1 — a run list row's displayed status: the backend's own `listStatus` (DELIVERING for
 * a COMPLETED run whose delivery is still out, REJECTED for a FAILED run a gate rejected) once an
 * updated backend sends it. On an older deploy that omits the field, the row still reads right
 * while the DELIVERING/REJECTED tab is active — every row the list sent back under that tab IS
 * that split status, even though its own `status` reads the plain COMPLETED/FAILED underneath.
 */
export function runListDisplayStatus(
  run: { status: string; listStatus?: string | null },
  activeTab: string,
): string {
  return run.listStatus ?? (SPLIT_TABS.has(activeTab) ? activeTab : run.status);
}
